import { apiUtils, endpoints } from 'api';

export const api = {
  login: async (data) => {
    return await apiUtils.post(endpoints.LOGIN, data, false);
  },
  profilePatch: async (data) => {
    return await apiUtils.patch(endpoints.PROFILE, data);
  },
  changePassword: async (data) => {
    return await apiUtils.post(endpoints.CHANGE_PASSWORD, data);
  },

  // User API calls
  getUsers: async (params = {}) => {
    return await apiUtils.get(endpoints.USERS, params);
  },
  searchUsers: async (query = '') => {
    return await apiUtils.get(endpoints.SEARCH_USERS, { q: query });
  },
  searchPlayers: async (query = '') => {
    return await apiUtils.get(endpoints.SEARCH_PLAYERS, { q: query });
  },
  createUser: async (data) => {
    return await apiUtils.post(endpoints.USERS, data);
  },
  getUserDetail: async (id) => {
    return await apiUtils.get(`${endpoints.USERS}${id}/`);
  },
  updateUser: async (id, data) => {
    return await apiUtils.patch(`${endpoints.USERS}${id}/`, data);
  },
  deleteUser: async (id) => {
    return await apiUtils.delete(`${endpoints.USERS}${id}/`);
  },

  // Role API calls
  getSubRoles: async (params = {}) => {
    return await apiUtils.get(endpoints.ROLES, params);
  },
  getSubRolesByRole: async () => {
    return await apiUtils.get(endpoints.SUBROLES_BY_ROLE());
  },
  createSubRole: async (data) => {
    return await apiUtils.post(endpoints.ROLES, data);
  },
  getSubRoleDetail: async (id) => {
    return await apiUtils.get(endpoints.ROLE_DETAIL(id));
  },
  updateSubRole: async (id, data) => {
    return await apiUtils.patch(endpoints.ROLE_DETAIL(id), data);
  },
  deleteSubRole: async (id) => {
    return await apiUtils.delete(endpoints.ROLE_DETAIL(id));
  },

  // Cities API calls
  getCities: async () => {
    return await apiUtils.get(endpoints.CITIES);
  },

  getGrounds: async (params = {}) => {
    return await apiUtils.get(endpoints.GROUNDS, params);
  },
  searchGrounds: async (query = '') => {
    return await apiUtils.get(endpoints.SEARCH_GROUNDS, { q: query });
  },
  createGround: async (data) => {
    // Check if data is FormData (for file uploads) or regular object
    if (data instanceof FormData) {
      return await apiUtils.post(endpoints.GROUNDS, data, true, true); // true for FormData
    } else {
      return await apiUtils.post(endpoints.GROUNDS, data);
    }
  },
  getGroundDetail: async (id) => {
    return await apiUtils.get(endpoints.GROUND_DETAIL(id));
  },
  updateGround: async (id, data) => {
    // Check if data is FormData (for file uploads) or regular object
    if (data instanceof FormData) {
      return await apiUtils.patch(endpoints.GROUND_DETAIL(id), data, true, true); // true for FormData
    } else {
      return await apiUtils.patch(endpoints.GROUND_DETAIL(id), data);
    }
  },
  deleteGround: async (id) => {
    return await apiUtils.delete(endpoints.GROUND_DETAIL(id));
  },
  approveGround: async (id, data) => {
    return await apiUtils.patch(endpoints.GROUND_APPROVE(id), data);
  },

  // Fields
  getFields: async (params = {}) => {
    return await apiUtils.get(endpoints.FIELDS, params);
  },
  createField: async (data) => {
    // Check if data is FormData (for file uploads) or regular object
    if (data instanceof FormData) {
      return await apiUtils.post(endpoints.FIELDS, data, true, true); // true for FormData
    } else {
      return await apiUtils.post(endpoints.FIELDS, data);
    }
  },
  getFieldDetail: async (id) => {
    return await apiUtils.get(endpoints.FIELDS_DETAIL(id));
  },
  getFieldDetailWithBookings: async (id, date) => {
    return await apiUtils.get(endpoints.FIELDS_DETAIL_WITH_BOOKINGS(id), { date: date });
  },
  updateField: async (id, data) => {
    if (data instanceof FormData) {
      return await apiUtils.patch(endpoints.FIELDS_DETAIL(id), data, true, true); // true for FormData
    } else {
      return await apiUtils.patch(endpoints.FIELDS_DETAIL(id), data);
    }
  },
  deleteField: async (id) => {
    return await apiUtils.delete(endpoints.FIELDS_DETAIL(id));
  },

  // Maintenance API calls
  getMaintenance: async (params = {}) => {
    return await apiUtils.get(endpoints.MAINTENANCE, params);
  },
  createMaintenance: async (data) => {
    return await apiUtils.post(endpoints.MAINTENANCE, data);
  },
  getMaintenanceDetail: async (id) => {
    return await apiUtils.get(endpoints.MAINTENANCE_DETAIL(id));
  },
  updateMaintenance: async (id, data) => {
    return await apiUtils.patch(endpoints.MAINTENANCE_DETAIL(id), data);
  },
  deleteMaintenance: async (id) => {
    return await apiUtils.delete(endpoints.MAINTENANCE_DETAIL(id));
  },
  getMaintenanceByGround: async (groundId) => {
    return await apiUtils.get(endpoints.MAINTENANCE_BY_GROUND(groundId));
  },
  getMaintenanceByField: async (fieldId) => {
    return await apiUtils.get(endpoints.MAINTENANCE_BY_FIELD(fieldId));
  },

  getSports: async () => {
    return await apiUtils.get(endpoints.SPORTS);
  },

  getAmenities: async () => {
    return await apiUtils.get(endpoints.AMENITIES);
  },

  // Booking API calls
  getBookings: async (params = {}) => {
    return await apiUtils.get(endpoints.BOOKINGS, params);
  },
  getBookingDetail: async (id) => {
    return await apiUtils.get(endpoints.BOOKING_DETAIL(id));
  },
  updateBookingStatus: async (id, data) => {
    return await apiUtils.patch(endpoints.BOOKING_STATUS(id), data);
  },
  getBookingCalendar: async (params = {}) => {
    return await apiUtils.get(endpoints.BOOKING_CALENDAR, params);
  },

  // Offline Booking API calls
  getOfflineBookings: async (params = {}) => {
    return await apiUtils.get(endpoints.OFFLINE_BOOKINGS, params);
  },
  createOfflineBooking: async (data) => {
    return await apiUtils.post(endpoints.OFFLINE_BOOKINGS, data);
  },
  getOfflineBookingDetail: async (id) => {
    return await apiUtils.get(endpoints.OFFLINE_BOOKING_DETAIL(id));
  },
  updateOfflineBookingStatus: async (id, data) => {
    return await apiUtils.patch(endpoints.OFFLINE_BOOKING_STATUS(id), data);
  },

  // Report API calls
  getReports: async (params = {}) => {
    return await apiUtils.get(endpoints.REPORTS, params);
  },
  getReportDetail: async (id) => {
    return await apiUtils.get(endpoints.REPORT_DETAIL(id));
  },
  resolveReport: async (id, data) => {
    return await apiUtils.patch(endpoints.REPORT_RESOLVE(id), data);
  },
  dismissReport: async (id, data) => {
    return await apiUtils.patch(endpoints.REPORT_DISMISS(id), data);
  },
  markReportUnderReview: async (id) => {
    return await apiUtils.patch(endpoints.REPORT_UNDER_REVIEW(id), {});
  },

  // Analytics API calls
  getAnalyticsKPIs: async (params = {}) => {
    return await apiUtils.get(endpoints.ANALYTICS_KPIS, params);
  },
  getBookingTrends: async (params = {}) => {
    return await apiUtils.get(endpoints.ANALYTICS_BOOKING_TRENDS, params);
  },
  getRevenueBreakdown: async (params = {}) => {
    return await apiUtils.get(endpoints.ANALYTICS_REVENUE_BREAKDOWN, params);
  },
  getGroundPerformance: async (params = {}) => {
    return await apiUtils.get(endpoints.ANALYTICS_GROUND_PERFORMANCE, params);
  },
  getUserActivity: async (params = {}) => {
    return await apiUtils.get(endpoints.ANALYTICS_USER_ACTIVITY, params);
  },
  getBookingHeatmap: async (params = {}) => {
    return await apiUtils.get(endpoints.ANALYTICS_BOOKING_HEATMAP, params);
  },
  exportAnalytics: async (params = {}) => {
    return await apiUtils.get(endpoints.ANALYTICS_EXPORT, params, true, true, true); // true for full response
  },

  // Ground Admin Earnings API calls
  getGroundAdminEarningsSummary: async (params = {}) => {
    return await apiUtils.get(endpoints.GROUND_ADMIN_EARNINGS_SUMMARY, params);
  },
  getGroundAdminBookingAnalytics: async (params = {}) => {
    return await apiUtils.get(endpoints.GROUND_ADMIN_BOOKING_ANALYTICS, params);
  },
  getGroundAdminRevenueBySport: async (params = {}) => {
    return await apiUtils.get(endpoints.GROUND_ADMIN_REVENUE_BY_SPORT, params);
  },
  getGroundAdminBookingHeatmap: async (params = {}) => {
    return await apiUtils.get(endpoints.GROUND_ADMIN_BOOKING_HEATMAP, params);
  },
  getGroundAdminTransactionLedger: async (params = {}) => {
    return await apiUtils.get(endpoints.GROUND_ADMIN_TRANSACTION_LEDGER, params);
  },
  getGroundAdminDailySlotOccupancy: async (params = {}) => {
    return await apiUtils.get(endpoints.GROUND_ADMIN_DAILY_SLOT_OCCUPANCY, params);
  },
  exportGroundAdminData: async (params = {}) => {
    return await apiUtils.get(endpoints.GROUND_ADMIN_EXPORT, params, true, true, true); // true for full response
  },
  getWithdrawalBalance: async () => {
    return await apiUtils.get(endpoints.GROUND_ADMIN_WITHDRAWAL_BALANCE);
  },
  getWithdrawalRequests: async (params = {}) => {
    return await apiUtils.get(endpoints.GROUND_ADMIN_WITHDRAWAL_REQUESTS, params);
  },
  createWithdrawalRequest: async () => {
    return await apiUtils.post(endpoints.GROUND_ADMIN_WITHDRAWAL_REQUESTS, {});
  },

  // Admin withdrawal API calls
  getAdminWithdrawalRequests: async (params = {}) => {
    return await apiUtils.get(endpoints.ADMIN_WITHDRAWAL_REQUESTS, params);
  },
  updateWithdrawalRequestStatus: async (id, data) => {
    return await apiUtils.patch(endpoints.ADMIN_WITHDRAWAL_REQUEST_STATUS(id), data);
  },

  // Notification API calls
  getNotifications: async (params = {}) => {
    return await apiUtils.get(endpoints.NOTIFICATIONS, params);
  },
  getNotificationDetail: async (id) => {
    return await apiUtils.get(endpoints.NOTIFICATION_DETAIL(id));
  },
  markNotificationsRead: async (data) => {
    return await apiUtils.post(endpoints.NOTIFICATIONS_MARK_READ, data);
  },
  markAllNotificationsRead: async () => {
    return await apiUtils.post(endpoints.NOTIFICATIONS_MARK_ALL_READ, {});
  },
  getUnreadNotificationCount: async () => {
    return await apiUtils.get(endpoints.NOTIFICATIONS_UNREAD_COUNT);
  },
};
